/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Table, VisuallyHidden } from '@radix-ui/themes';

import { SortableTableHeader, type SortDirection } from '../../../../shared/SortableTableHeader';
import { EvaluationsTableRow } from './EvaluationsTableRow';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';
import type { EvaluationRow, EvaluationSortField } from './evaluations.types';

const SORTABLE_COLUMNS: { sortKey: EvaluationSortField; label: string }[] = [
  { sortKey: 'PUBLIC_ID', label: STRINGS.columns.repository },
  { sortKey: 'FORMAT', label: STRINGS.columns.format },
];

const COUNT_COLUMNS: { sortKey: EvaluationSortField; label: string }[] = [
  { sortKey: 'COMPONENTS_EVALUATED', label: STRINGS.columns.componentsEvaluated },
  { sortKey: 'CRITICAL_VIOLATION_COUNT', label: STRINGS.columns.critical },
  { sortKey: 'SEVERE_VIOLATION_COUNT', label: STRINGS.columns.severe },
  { sortKey: 'MODERATE_VIOLATION_COUNT', label: STRINGS.columns.moderate },
];

export interface EvaluationsTableProps {
  rows: EvaluationRow[];
  sortBy: EvaluationSortField;
  asc: boolean;
  onSort: (sortBy: EvaluationSortField, asc: boolean) => void;
  /** The user may re-evaluate repositories: every row gets the action. */
  canEvaluate: boolean;
  /** Called when a re-evaluation of one of the rows ends, so the list shows the new counts. */
  onReevaluated: () => void;
}

export function EvaluationsTable({
  rows,
  sortBy,
  asc,
  onSort,
  canEvaluate,
  onReevaluated,
}: EvaluationsTableProps): JSX.Element {
  const handleSort = (key: string, direction: SortDirection) => onSort(key as EvaluationSortField, direction === 'asc');
  const sortProps = {
    currentSortKey: sortBy,
    currentSortDirection: (asc ? 'asc' : 'desc') as SortDirection,
    onSort: handleSort,
  };

  return (
    <Table.Root variant="surface" size="2">
      <caption>
        <VisuallyHidden>{STRINGS.list.tableCaption}</VisuallyHidden>
      </caption>
      <Table.Header>
        <Table.Row>
          {SORTABLE_COLUMNS.map(({ sortKey, label }) => (
            <SortableTableHeader key={sortKey} sortKey={sortKey} {...sortProps}>
              {label}
            </SortableTableHeader>
          ))}
          <Table.ColumnHeaderCell>{STRINGS.columns.quarantine}</Table.ColumnHeaderCell>
          <SortableTableHeader sortKey="LAST_EVALUATION_TIME" {...sortProps}>
            {STRINGS.columns.lastEvaluation}
          </SortableTableHeader>
          {COUNT_COLUMNS.map(({ sortKey, label }) => (
            <SortableTableHeader key={sortKey} sortKey={sortKey} {...sortProps}>
              {label}
            </SortableTableHeader>
          ))}
          <Table.ColumnHeaderCell>{STRINGS.columns.quarantined}</Table.ColumnHeaderCell>
          {canEvaluate && (
            <Table.ColumnHeaderCell>
              <VisuallyHidden>{STRINGS.reevaluate.actionsColumn}</VisuallyHidden>
            </Table.ColumnHeaderCell>
          )}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row, index) => (
          <EvaluationsTableRow
            key={row.repositoryId || `${row.name}-${index}`}
            row={row}
            canEvaluate={canEvaluate}
            onReevaluated={onReevaluated}
          />
        ))}
      </Table.Body>
    </Table.Root>
  );
}
